import type BooleanController from './Boolean/Controller'
import type FieldController from './Controller'
import type DateController from './Date/Controller'
import type MultiSelectController from './MultiSelect/Controller'
import type NumberController from './Number/Controller'
import type SelectController from './Select/Controller'
import type StringController from './String/Controller'

import React from 'react'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { isNumber } from 'lodash/fp'

import { Operators } from '../constants'
import {
	expectedArrayValue,
	expectedDateRangeValue,
	expectedDateValue,
	expectedNumberRangeValue,
} from '../helpers'
import {
	arrayToString,
	getUTCDate,
	isBooleanOperation,
	stringToArray,
} from '../utils/utils'

type FilterOutputValue<T extends FieldController = FieldController> =
	| (T extends DateController
			?
					| {
							from: Date | undefined
							to: Date | undefined
					  }
					| Date
					| undefined
			: T extends StringController
				? string | string[]
				: T extends NumberController
					? number | number[] | string | string[]
					: T extends BooleanController
						? string
						: T extends SelectController
							? string[] | string
							: T extends MultiSelectController
								? string[] | string
								: undefined)
	| undefined
type FilterInputValue<T extends FieldController = FieldController> =
	T extends DateController
		? [string, string] | string | []
		: T extends StringController
			? string | string[]
			: T extends NumberController
				? string | [string, string | number] | [string | number, string]
				: T extends BooleanController
					? string
					: T extends SelectController
						? string[] | string
						: T extends MultiSelectController
							? string[] | string
							: undefined

export type FilterField<T extends FieldController = FieldController> = {
	/**
	 * The field to manage the state of
	 */
	field: T
	/**
	 * Get the current value of the filter field
	 */
	getFilterValue: () => FilterOutputValue<T> | undefined
	/**
	 * Set the value of the filter field
	 * @param value - The value to set
	 */
	setFilterValue: (value: FilterInputValue<T>) => void
	/**
	 * Get the current reference of the filter field
	 */
	getFilterReference: () => string | undefined
	/**
	 * Set the reference of the filter field
	 * @param reference - The reference to set
	 */
	setFilterReference: (reference: string) => void
	/**
	 * Get the current mode of the filter field
	 */
	getFilterMode: () => 'reference' | 'value'
	/**
	 * Set the mode of the filter field
	 * @param mode - The mode to set
	 */
	setFilterMode: (mode: 'reference' | 'value') => void
	/**
	 * Get the current operation of the filter field
	 */
	getFilterOperation: () => Operators
	/**
	 * Set the operation of the filter field
	 * @param operation - The operation to set
	 */
	setFilterOperation: (operation: Operators) => void
	/**
	 * Set the value of the filter field
	 * @param operation - The operation to set
	 * @param value - The value to set
	 */
	setFilter: (operation: Operators, value: FilterInputValue<T>) => void
}

type State<T extends FieldController = FieldController> = {
	operation: Operators
	value?: FilterInputValue<T>
	reference?: string
}

/**
 * Format the value of the filter field
 * @param value - The value to format
 * @returns The formatted value
 */
const formatValue = (value: string | number | undefined) => {
	if (value === undefined || value === '') return ''
	const numValue = Number(value)

	return Number.isNaN(numValue) ? '' : numValue
}

/**
 * Get the range value of the filter field
 * @param _value - The value to get the range value of
 * @returns The range value of the filter field
 */
const getRangeValue = (_value: (string | number)[] | string | number) => {
	if (Array.isArray(_value)) return _value.map(formatValue)

	return [formatValue(_value), '']
}

const getDateRangeValue = (_value?: string[]) => ({
	from: _value?.[0] ? getUTCDate(new Date(_value?.[0])) : undefined,
	to: _value?.[1] ? getUTCDate(new Date(_value?.[1])) : undefined,
})

/**
 * Get the date value of the filter field
 * @param _value - The value to get the date value of
 * @returns The date value of the filter field
 */
const getDateValue = (_value?: string) =>
	_value ? getUTCDate(new Date(_value)) : undefined

/**
 * Get the array value of the filter field
 * @param _value - The value to get the array value of
 * @returns The array value of the filter field
 */
const getArrayValue = (_value?: string | string[]) =>
	stringToArray(
		isNumber(_value) ? String(_value) : (_value as string)
	) as string[]

/**
 * A hook that manages the state of a filter field
 * @param field - The field to manage the state of
 * @param initialState - The initial state of the filter field
 * @param onChange - The function to call when the state changes
 */
export const useFilterField = <T extends FieldController>(
	field: T,
	{
		initialState,
		onChange,
	}: {
		initialState: State<T>
		onChange: (
			state:
				| Pick<State<T>, 'operation' | 'value'>
				| Pick<State<T>, 'operation' | 'reference'>
		) => void
	}
): FilterField<T> => {
	const [state, setState] = useControllableState({
		prop: initialState,
		defaultProp: field.getInitialValue?.() as State<T>,
		onChange,
	})

	const [mode, setMode] = React.useState<'reference' | 'value'>(() =>
		Object.hasOwn(initialState, 'reference') ? 'reference' : 'value'
	)

	/**
	 * Get the current value of the filter field
	 */
	const getFilterValue = React.useCallback((): FilterOutputValue<T> => {
		if (expectedNumberRangeValue(state)) {
			return getRangeValue(state.value) as FilterOutputValue<T>
		} else if (expectedDateRangeValue(state)) {
			return getDateRangeValue(state.value) as FilterOutputValue<T>
		} else if (expectedDateValue(state)) {
			return getDateValue(state.value) as FilterOutputValue<T>
		} else if (expectedArrayValue(state)) {
			return getArrayValue(state.value) as FilterOutputValue<T>
		} else {
			return state.value as FilterOutputValue<T>
		}
	}, [state.operation, state.value])

	/**
	 * Set the value of the filter field
	 * @param value - The value to set
	 */
	const setFilterValue = React.useCallback(
		(value: FilterInputValue<T>) => {
			setState((prevState) => ({
				operation: prevState.operation,
				value,
			}))
		},
		[state.operation, state.value]
	)
	const getFilterReference = React.useCallback(
		() => state.reference,
		[state.reference]
	)
	/**
	 * Set the reference of the filter field
	 * @param reference - The reference to set the filter field to
	 */
	const setFilterReference = React.useCallback(
		(reference: string) => {
			setState({ ...state, reference })
		},
		[state.operation, state.reference]
	)

	/**
	 * Get the current mode of the filter field
	 */
	const getFilterMode = React.useCallback(() => mode, [mode])

	/**
	 * Set the mode of the filter field
	 * @param mode - The mode to set
	 */
	const setFilterMode = React.useCallback(
		(mode: 'reference' | 'value') => {
			setMode(mode)
			if (mode === 'reference') {
				setState((prevState) => ({
					operation: prevState.operation,
					reference: undefined,
				}))
			} else {
				setState((prevState) => ({
					operation: prevState.operation,
					value: undefined,
				}))
			}
		},
		[state.operation, state.reference, state.value]
	)

	/**
	 * Get the current operation of the filter field
	 */
	const getFilterOperation = React.useCallback(
		() => state.operation,
		[state.operation]
	)

	/**
	 * Set the operation of the filter field
	 * @param operation - The operation to set
	 */
	const setFilterOperation = React.useCallback(
		(operation: Operators) => {
			if (
				mode === 'reference' &&
				![
					Operators.BETWEEN,
					Operators.IS_EMPTY,
					Operators.IS_NOT_EMPTY,
				].includes(operation)
			) {
				setState({ ...state, operation, reference: state.reference })
			} else {
				const newStateDraft = { ...state, operation }

				if (expectedNumberRangeValue(newStateDraft)) {
					setState((prevState) => ({
						operation,
						value: getRangeValue(
							prevState.value as string | number | string[] | number[]
						) as FilterInputValue<T>,
					}))
				} else if (isBooleanOperation(operation)) {
					setState({
						operation,
						value: '1' as FilterInputValue<T>,
					})
				} else if (expectedDateRangeValue(newStateDraft)) {
					setState((prevState) => ({
						operation,
						value: (isSameOperationType(operation, prevState.operation)
							? prevState.value
							: []) as FilterInputValue<T>,
					}))
				} else if (expectedDateValue(newStateDraft)) {
					setState((prevState) => ({
						operation,
						value: isSameOperationType(operation, prevState.operation)
							? prevState.value
							: undefined,
					}))
				} else if (expectedArrayValue(newStateDraft)) {
					setState((prevState) => ({
						operation,
						value: stringToArray(
							isNumber(prevState.value)
								? String(prevState.value)
								: (prevState.value as string)
						) as FilterInputValue<T>,
					}))
				} else {
					setState((prevState) => ({
						operation,
						value: arrayToString(
							prevState.value as string[] | string
						) as FilterInputValue<T>,
					}))
				}
			}
		},
		[state.operation, state.reference, state.value, mode]
	)
	const setFilter = React.useCallback(
		(operation: Operators, value: FilterInputValue<T>) => {
			setState(() => ({
				operation,
				value,
			}))
		},
		[state.operation, state.reference, state.value, mode]
	)

	return {
		field,
		/* Value */
		getFilterValue,
		setFilterValue,
		/* Reference */
		getFilterReference,
		setFilterReference,
		/* Mode */
		getFilterMode,
		setFilterMode,
		/* Operation */
		getFilterOperation,
		setFilterOperation,
		/* State */
		setFilter,
	}
}

/**
 * Check if the operation of date field is basic
 * @param operation - The operation to check
 * @returns True if the operation is basic, false otherwise.
 * Basic operations are:
 * - DATE_AFTER
 * - DATE_BEFORE
 * - DATE_AT
 */
const isBasic = (operation: Operators) =>
	operation !== Operators.DATE_BETWEEN && !isBooleanOperation(operation)

/**
 * Check if the operation of date field is the same type as the previous operation
 * @param newOperation - The new operation to check
 * @param prevOperation - The previous operation to check
 * @returns True if the operation is the same type, false otherwise
 */
const isSameOperationType = (
	newOperation: Operators,
	prevOperation: Operators
) => {
	const isBoolean =
		isBooleanOperation(newOperation) && isBooleanOperation(prevOperation)
	const isBetween =
		newOperation === Operators.DATE_BETWEEN &&
		prevOperation === Operators.DATE_BETWEEN

	const isSingle = isBasic(newOperation) && isBasic(prevOperation)

	return isBoolean || isBetween || isSingle
}
